/**
 * FE item-id plane helpers (SPA S7 / dual-vocab M7).
 * Backend #9 resolves semantic ↔ UUID on write/detail; attention-manifest
 * ``source=db`` emits postgresItemId / semanticItemId aliases. SPA must not
 * invent the other plane when aliases are absent.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SEMANTIC_ITEM_RE = /^(co-|ai-)/i;

export function isUuidItemId(id: string | null | undefined): boolean {
  return Boolean(id && UUID_RE.test(id.trim()));
}

export function isSemanticItemId(id: string | null | undefined): boolean {
  const raw = (id ?? '').trim();
  return Boolean(raw && SEMANTIC_ITEM_RE.test(raw));
}

/** Normalize for equality: trim; UUIDs compared case-insensitively. */
export function normalizeItemIdKey(id: string | null | undefined): string {
  const raw = (id ?? '').trim();
  if (!raw) return '';
  return isUuidItemId(raw) ? raw.toLowerCase() : raw;
}

export function itemIdsEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = normalizeItemIdKey(a);
  const right = normalizeItemIdKey(b);
  if (!left || !right) return false;
  return left === right;
}

export type ItemIdMatchOptions = {
  /**
   * When true, also treat ``improvementRef`` as an identity key.
   * Default false so pending-nav / Exo lookup do not open an activity for an
   * improvement id unless the caller opts in (Guidance decompose).
   */
  matchImprovementRef?: boolean;
};

/** Fields that may carry an alternate id plane on queue / detail rows. */
export type ItemIdAliasSource = {
  itemId?: string;
  improvementRef?: string;
  /** Explicit dual-plane aliases when the API emits them. */
  itemIdAliases?: string[];
  postgresItemId?: string;
  semanticItemId?: string;
  kryspaceItemId?: string;
};

export function collectItemIdKeys(
  source: ItemIdAliasSource | null | undefined,
  options?: ItemIdMatchOptions
): string[] {
  if (!source) return [];
  const raw = [
    source.itemId,
    source.postgresItemId,
    source.semanticItemId,
    source.kryspaceItemId,
    ...(source.itemIdAliases ?? []),
  ];
  if (options?.matchImprovementRef) {
    raw.push(source.improvementRef);
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of raw) {
    const key = normalizeItemIdKey(id);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/** True when ``needle`` matches any identity key on ``source``. */
export function itemIdMatchesSource(
  needle: string | null | undefined,
  source: ItemIdAliasSource | null | undefined,
  options?: ItemIdMatchOptions
): boolean {
  const want = normalizeItemIdKey(needle);
  if (!want) return false;
  return collectItemIdKeys(source, options).includes(want);
}
